import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, Copy, Repeat, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import moment from "moment";
import { MediaPreviewDialog } from "./MediaPreview";
import { AttachmentPreview } from "@/common/components/attachment-preview/attachment-preview";
import { blobMap } from "@common-services/blob-service";
import { handleMentionsFormatInMessage } from "@common-services/utils";
import { UserMessage } from "@bt-common/schemas/bt/user-message";
import { StatusCell } from "./StatusCell";
import { ContactCell } from "./ContactCell";
import { selectRecurrenceInfoFromMessage } from "@common-reducers/CampaignSelectors";
import { useAppSelector } from "@common-reducers/hooks/store.hook";

interface MessageCardProps {
    message: UserMessage;
    onMediaClick?: (message: UserMessage) => void;
}

function MessagePreview({ message }: { message: UserMessage }) {
    if (!message) return null;

    const messageText = message.message || '';
    const hasImage = message.imageId || message.asset?.url;

    return (
        <div className="space-y-3">
            {/* Message body */}
            {messageText ? (
                <div className="text-sm whitespace-pre-wrap break-words text-slate-900 dark:text-slate-100">
                    {handleMentionsFormatInMessage(messageText)}
                </div>
            ) : (
                <span className="text-slate-400 dark:text-slate-500 italic text-sm">No text content</span>
            )}
            
            {/* Image/Media preview */}
            {hasImage && (
                <div className="mt-2 max-w-md mx-auto rounded-lg overflow-hidden" style={{ height: '300px', maxHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="w-full h-full relative rounded-lg overflow-hidden">
                        <AttachmentPreview
                            attachment={message.imageId ? blobMap.get(message.imageId) : null}
                            attachmentUrl={message.asset?.url}
                            attachmentType={message.asset?.type ?? undefined}
                            attachmentName={message.asset?.name ?? undefined}
                            previewMaxHeight="300px"
                            imageObjectFit="contain"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function ContactDisplay({ contact }: { contact?: any }) {
    if (!contact?.id) return null;
    
    return (
        <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500 dark:text-slate-400">Contact:</span>
            <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                {contact.name || contact.id}
            </code>
        </div>
    );
}

export function MessageCard({ message, onMediaClick }: MessageCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<UserMessage | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const messageId = message._id || '';
    const contact = message?.contactList?.[0];

    // Get recurrence info using selector (works directly with UserMessage)
    const recurrenceInfo = useAppSelector(state => 
        selectRecurrenceInfoFromMessage(state, message)
    );

    if (!message) return null;

    const handleCopyId = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleMediaClick = (msg: UserMessage) => {
        if (onMediaClick) {
            onMediaClick(msg);
        } else {
            setSelectedMedia(msg);
        }
    };

    return (
        <>
            <Card className="overflow-hidden border-slate-200 dark:border-slate-700">
                {/* Top Summary Header */}
                <CardHeader className="pb-3 bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            {/* Status and Info Row */}
                            <div className="flex items-center gap-4 flex-wrap mb-3">
                                <StatusCell status={message.status} dueDate={message.dueDate} />
                                <div className="flex flex-wrap items-center gap-4 text-sm">
                                    {message.dueDate && (
                                        <div className="text-slate-500 dark:text-slate-400 text-xs">
                                            <span className="font-medium">Due Date: </span>
                                            <span>{moment(message.dueDate).format('DD/MM/YYYY, h:mm:ss A')}</span>
                                        </div>
                                    )}
                                    {message.createdAt && (
                                        <div className="text-slate-500 dark:text-slate-400 text-xs">
                                            <span className="font-medium">Created: </span>
                                            <span>{moment(message.createdAt).format('DD/MM/YYYY, h:mm:ss A')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Message Content */}
                            <MessagePreview message={message} />
                            
                            {/* Contact and Campaign Info */}
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600 dark:text-slate-300">
                                <ContactDisplay contact={contact} />
                                {message?.isRecurring && (
                                    <Badge variant="outline" className="bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800 flex items-center gap-1">
                                        <Repeat className="h-3 w-3" />
                                        Recurring
                                    </Badge>
                                )}
                                {message?.campaignId && (
                                    <>
                                        <Badge variant="outline" className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600">
                                            Campaign
                                        </Badge>
                                        <div className="flex items-center gap-2">
                                            <span className="text-slate-500 dark:text-slate-400">Campaign ID:</span>
                                            <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                                                {message.campaignId}
                                            </code>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Recurrence Schedule Explanation */}
                            {recurrenceInfo && (
                                <div className="mt-3 p-3 bg-purple-50/50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-md">
                                    <div className="flex items-start gap-2">
                                        <Repeat className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-purple-900 dark:text-purple-200 mb-1">
                                                Recurrence Schedule
                                            </div>
                                            <div className="text-sm text-purple-800 dark:text-purple-300 mb-2">
                                                {recurrenceInfo.recurrenceText}
                                            </div>
                                            {recurrenceInfo.nextDueDate && (
                                                <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-400 mt-2 pt-2 border-t border-purple-200 dark:border-purple-700">
                                                    <Calendar className="h-3 w-3" />
                                                    <span className="font-medium">Next due date:</span>
                                                    <span>{recurrenceInfo.nextDueDate.format('DD/MM/YYYY, h:mm:ss A')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyId({ stopPropagation: () => {} } as any, messageId)}
                            className="h-8 w-8 p-0 shrink-0"
                        >
                            <Copy className={copiedId === messageId ? "h-4 w-4 text-green-600 dark:text-green-400" : "h-4 w-4 text-slate-600 dark:text-slate-400"} />
                        </Button>
                    </div>
                </CardHeader>

                {/* Footer - Full message JSON details (collapsed by default) */}
                <Separator className="bg-slate-200 dark:border-slate-700" />
                <CardFooter className="pt-3 pb-3">
                    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                        <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                                {isExpanded ? (
                                    <>
                                        <ChevronUp className="h-3 w-3 mr-1" />
                                        Hide Details
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className="h-3 w-3 mr-1" />
                                        Show Details
                                    </>
                                )}
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="w-full mt-2">
                            <div className="space-y-2 text-sm text-slate-900 dark:text-slate-100">
                                <Separator className="bg-slate-200 dark:border-slate-700" />
                                <div>
                                    <strong>Message ID:</strong>
                                    <div className="flex items-center gap-2 mt-1">
                                        <code className="flex-1 text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                                            {messageId}
                                        </code>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopyId(e as any, messageId);
                                            }}
                                        >
                                            <Copy className={copiedId === messageId ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                        </Button>
                                    </div>
                                </div>
                                {message?.dueDate && (
                                    <div>
                                        <strong>Due Date:</strong>
                                        <span className="ml-2">{moment(message.dueDate).format('DD/MM/YYYY, h:mm:ss A')}</span>
                                    </div>
                                )}
                                <div>
                                    <strong>Full Message:</strong>
                                    <pre className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs overflow-auto max-h-40 text-slate-900 dark:text-slate-200">
                                        {JSON.stringify(message, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                </CardFooter>
            </Card>

            <MediaPreviewDialog 
                open={!!selectedMedia}
                onClose={() => setSelectedMedia(null)}
                message={selectedMedia}
            />
        </>
    );
}

